import { createContext, useContext, useState, type ReactNode } from 'react';
import type { CustomNode } from '../types/nodes';

interface ClipboardData {
  type: 'node';
  data: CustomNode;
  timestamp: number;
}

interface ClipboardContextType {
  clipboardData: ClipboardData | null;
  copyNode: (node: CustomNode) => void;
  pasteNode: () => CustomNode | null;
  clearClipboard: () => void;
  hasNodeInClipboard: boolean;
}

const ClipboardContext = createContext<ClipboardContextType | null>(null);

export function useClipboard() {
  const context = useContext(ClipboardContext);
  if (!context) {
    throw new Error('useClipboard must be used within a ClipboardProvider');
  }
  return context;
}

interface ClipboardProviderProps {
  children: ReactNode;
}

export function ClipboardProvider({ children }: ClipboardProviderProps) {
  const [clipboardData, setClipboardData] = useState<ClipboardData | null>(null);

  const copyNode = (node: CustomNode) => {
    // Create a deep copy of the node with a new ID
    const copiedNode: CustomNode = {
      ...node,
      id: `${node.type}-${Date.now()}`, // Generate new unique ID
      position: { ...node.position }, // Copy position
      data: {
        ...node.data,
        label: `${node.data.label} (Copy)`, // Add "(Copy)" to label
      },
    };

    setClipboardData({
      type: 'node',
      data: copiedNode,
      timestamp: Date.now(),
    });
  };

  const pasteNode = (): CustomNode | null => {
    if (!clipboardData || clipboardData.type !== 'node') {
      return null;
    }

    // Check if clipboard data is not too old (24 hours)
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    if (Date.now() - clipboardData.timestamp > maxAge) {
      setClipboardData(null);
      return null;
    }

    // Return a fresh copy of the node with updated position
    const pastedNode: CustomNode = {
      ...clipboardData.data,
      id: `${clipboardData.data.type}-${Date.now()}`, // Generate new unique ID
      position: { ...clipboardData.data.position }, // Copy position
    };

    return pastedNode;
  };

  const clearClipboard = () => {
    setClipboardData(null);
  };

  const hasNodeInClipboard = clipboardData?.type === 'node';

  return (
    <ClipboardContext.Provider
      value={{
        clipboardData,
        copyNode,
        pasteNode,
        clearClipboard,
        hasNodeInClipboard,
      }}
    >
      {children}
    </ClipboardContext.Provider>
  );
}